import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Cell } from "recharts";
import { Users, UserPlus, Activity, Wallet, Lightbulb, Brain, Mic, MessageCircle, CalendarCheck } from "lucide-react";

const SYM = { eur: "€", gbp: "£", usd: "$" };
const PLAN = { individual_monthly: "Individual", club_monthly: "Club", starter_monthly: "Initiate (old)", pro_monthly: "Operator (old)", elite_monthly: "Fleet Command (old)" };
const STATUS = { active: ["Paying", "#CCFF00"], trialing: ["On taster", "#3D7BFF"], trial_ended: ["Taster ended", "#FFB340"],
                 past_due: ["Payment failed", "#FF6B6B"], cancelled: ["Cancelled", "#8A91A6"], inactive: ["Inactive", "#565C6F"] };

export function InsightsPanel({ endpoint = "/api/admin/insights", title = "Insights", overline = "how members use OCTON Gym" }) {
  const [days, setDays] = useState(30);
  const { data: d } = useQuery({ queryKey: ["insights", endpoint, days], queryFn: async () => (await api.get(endpoint, { params: { days } })).data });
  if (!d) return <p className="mono-label animate-pulse">Loading insights…</p>;
  const tip = { contentStyle: { background: "#0F1115", border: "1px solid #1E222A", borderRadius: 8, fontSize: 12 }, labelStyle: { color: "#8A91A6" } };
  const club = !d.revenue;
  const mrr = Object.entries(d.revenue?.mrr_by_currency || {});
  const top = d.funnel[0]?.members || 1;

  return (
    <div className="space-y-5" data-testid="insights-panel">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <span className="mono-label text-acid">{overline}</span>
          <h2 className="font-heading font-black text-2xl tracking-tight">{title}</h2>
        </div>
        <div className="inline-flex rounded-full border border-[#343A4A] p-1">
          {[7, 30, 90].map((n) => (
            <button key={n} onClick={() => setDays(n)} className={`px-4 h-8 rounded-full text-sm ${days === n ? "bg-acid text-black" : "text-[#8A91A6]"}`}>{n} days</button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi icon={Users} c="#3D7BFF" label={club ? "Athletes" : "Members"} value={d.members.total} sub={`${d.members.active_7d} active this week`} />
        <Kpi icon={UserPlus} c="#CCFF00" label="New sign-ups" value={days === 7 ? d.members.new_7d : d.members.new_30d} sub={days === 7 ? "last 7 days" : "last 30 days"} />
        {club ? (
          <Kpi icon={CalendarCheck} c="#FFB340" label="Booking classes" value={d.engagement.class_bookers} sub="athletes with a booking" />
        ) : (
          <Kpi icon={Wallet} c="#FFB340" label="Monthly revenue" value={mrr.length ? mrr.map(([c, v]) => `${SYM[c] || ""}${v}`).join(" · ") : "–"} sub="recurring, incl. VAT" />
        )}
        {club ? (
          <Kpi icon={Activity} c="#B57BFF" label="Logging training" value={d.engagement.training_loggers} sub="athletes this period" />
        ) : (
          <Kpi icon={Activity} c="#B57BFF" label="Paying members" value={d.members.paying_ever} sub={`${Math.round(100 * d.members.paying_ever / Math.max(d.members.total, 1))}% of sign-ups`} />
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
        {/* Funnel */}
        <div className="lg:col-span-6 rounded-xl border border-[#1E222A] bg-[#0F1115] p-5">
          <p className="font-heading font-bold">Member journey</p>
          <p className="text-xs text-[#8A91A6] mb-3">{club ? "How far your athletes have got since joining the club." : "Where members drop off between signing up and paying."}</p>
          <div className="space-y-2.5">
            {d.funnel.filter((f) => !club || !/paying/i.test(f.step)).map((f, i) => (
              <div key={f.step}>
                <div className="flex justify-between text-sm"><span>{f.step}</span><span className="font-mono2 text-[#B7BECB]">{f.members} · {Math.round(100 * f.members / top)}%</span></div>
                <div className="h-2.5 rounded-full bg-[#1E222A] overflow-hidden mt-1">
                  <motion.div initial={{ width: 0 }} animate={{ width: `${(100 * f.members) / top}%` }} transition={{ duration: 0.8, delay: i * 0.08 }}
                    className="h-full rounded-full" style={{ background: "linear-gradient(90deg,#3D7BFF,#CCFF00)" }} />
                </div>
              </div>
            ))}
          </div>
          <div className="flex flex-wrap gap-2 mt-4">
            {!club && Object.entries(d.members.by_status).map(([k, v]) => (
              <span key={k} className="mono-label rounded-full px-2.5 py-1 border" style={{ borderColor: `${(STATUS[k] || [k, "#8A91A6"])[1]}66`, color: (STATUS[k] || [k, "#8A91A6"])[1] }}>
                {(STATUS[k] || [k])[0]} {v}
              </span>
            ))}
          </div>
        </div>

        {/* Feature usage */}
        <div className="lg:col-span-6 rounded-xl border border-[#1E222A] bg-[#0F1115] p-5">
          <p className="font-heading font-bold">What's popular</p>
          <p className="text-xs text-[#8A91A6] mb-2">{club ? "Athletes who opened each training area of the app." : "Members who opened each part of the app."}</p>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={d.features.slice(0, 10)} layout="vertical" margin={{ left: 10 }}>
                <XAxis type="number" hide />
                <YAxis type="category" dataKey="page" width={110} tick={{ fill: "#B7BECB", fontSize: 12 }} axisLine={false} tickLine={false} />
                <Tooltip {...tip} cursor={{ fill: "rgba(255,255,255,0.04)" }} formatter={(v, n) => [v, n === "members" ? "Members" : n]} />
                <Bar dataKey="members" radius={[0, 6, 6, 0]}>
                  {d.features.slice(0, 10).map((f, i) => <Cell key={f.path} fill={i === 0 ? "#CCFF00" : "#3D7BFF"} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
        {/* Engagement */}
        <div className="lg:col-span-4 rounded-xl border border-[#1E222A] bg-[#0F1115] p-5 space-y-3">
          <p className="font-heading font-bold">{club ? "Squad engagement" : "Coach engagement"}</p>
          {!club && <Row icon={MessageCircle} label="Coach messages" value={`${d.engagement.coach_messages} from ${d.engagement.coach_users} members`} />}
          {!club && <Row icon={Mic} label="Voice sessions" value={Object.values(d.engagement.voice_sessions || {}).reduce((a, b) => a + b, 0)} />}
          <Row icon={Activity} label={club ? "Athletes logging training" : "Members logging training"} value={d.engagement.training_loggers} />
          <Row icon={CalendarCheck} label={club ? "Athletes booking classes" : "Members booking classes"} value={d.engagement.class_bookers} />
          {!club && <Row icon={Brain} label="Wellbeing check-ins" value={d.engagement.checkins ? `${d.engagement.checkins} · mood ${d.engagement.avg_mood}/5 · stress ${d.engagement.avg_stress}/5` : "0"} />}
          {club && <p className="text-xs text-[#565C6F] pt-2 border-t border-[#1E222A]">Coach chats, voice sessions, wellbeing check-ins and payments stay private to each athlete.</p>}
          {!club && Object.keys(d.revenue.plans).length > 0 && (
            <div className="pt-2 border-t border-[#1E222A]">
              <p className="mono-label mb-1.5">plans</p>
              {Object.entries(d.revenue.plans).map(([k, v]) => <p key={k} className="text-sm flex justify-between"><span>{PLAN[k] || k}</span><span className="text-acid">{v}</span></p>)}
            </div>
          )}
        </div>

        {/* Classes */}
        <div className="lg:col-span-4 rounded-xl border border-[#1E222A] bg-[#0F1115] p-5">
          <p className="font-heading font-bold mb-3">Class popularity</p>
          <div className="space-y-2.5">
            {d.classes.length === 0 && <p className="text-sm text-[#8A91A6]">No classes in this period yet.</p>}
            {d.classes.slice(0, 7).map((cl, i) => (
              <div key={`${cl.title}-${i}`}>
                <div className="flex justify-between text-sm"><span className="truncate">{cl.title}{cl.starts_at && <span className="text-[#565C6F]"> · {new Date(cl.starts_at).toLocaleDateString(undefined, { day: "numeric", month: "short" })}</span>}</span><span className="text-[#B7BECB]">{cl.booked}/{cl.capacity}</span></div>
                <div className="h-1.5 rounded-full bg-[#1E222A] overflow-hidden mt-1">
                  <div className="h-full rounded-full" style={{ width: `${cl.fill_pct}%`, background: cl.fill_pct > 75 ? "#CCFF00" : cl.fill_pct > 30 ? "#3D7BFF" : "#FFB340" }} />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Opportunities */}
        <div className="lg:col-span-4 relative overflow-hidden rounded-xl border border-acid/30 bg-gradient-to-br from-[#12160A] to-[#0B0D11] p-5">
          <p className="font-heading font-bold flex items-center gap-2"><Lightbulb className="w-4 h-4 text-acid" /> Opportunities</p>
          <ul className="mt-3 space-y-2.5">
            {d.opportunities.map((o) => <li key={o} className="text-sm text-[#D7DCE5] flex gap-2"><span className="text-acid">•</span>{o}</li>)}
          </ul>
          {!club && <p className="mono-label mt-4 flex items-center gap-1.5"><Brain className="w-3.5 h-3.5" /> shared with OCTON DNA as anonymous totals</p>}
        </div>
      </div>
    </div>
  );
}

function Kpi({ icon: Icon, c, label, value, sub }) {
  return (
    <div className="relative overflow-hidden rounded-xl p-5 border border-[#1E222A] bg-[#0F1115]">
      <div className="absolute -top-10 -right-10 w-32 h-32 rounded-full blur-2xl opacity-25" style={{ background: c }} />
      <span className="relative w-10 h-10 rounded-lg flex items-center justify-center mb-3" style={{ background: `${c}1F`, border: `1px solid ${c}55` }}><Icon className="w-5 h-5" style={{ color: c }} /></span>
      <p className="relative font-heading font-black text-2xl">{value}</p>
      <p className="relative mono-label mt-1">{label}</p>
      <p className="relative text-xs text-[#8A91A6] mt-1">{sub}</p>
    </div>
  );
}

function Row({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center gap-3 text-sm">
      <Icon className="w-4 h-4 text-[#8A91A6] shrink-0" />
      <span className="flex-1 text-[#B7BECB]">{label}</span>
      <span className="text-white">{value}</span>
    </div>
  );
}
